import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeEnExtra from '@angular/common/locales/extra/en';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideEnvironmentInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  ApiModule as IncentiveSupportApiModule,
  Configuration as IncentiveSupportApiConfiguration
} from '@coin/customer/incentive-support/data-access';
import { AuthUserState } from '@coin/shared/auth/data-access';
import { AuthComponentsModule, AuthGmsAngularClientModule, UserState } from '@coin/shared/auth/feature';
import { AuthGmsClientConfig } from '@coin/shared/auth/util';
import { ApiModule as ApiModuleV2, Configuration as ApiConfigurationV2 } from '@coin/shared/core/data-access-v2';
import {
  AppEnvironment,
  CustomTranslationLoader,
  provideTranslateDebugStore
} from '@coin/shared/core/data-access-translation-loader';
import { provideInjectablePipes } from '@coin/shared/core/feature-pipes';
import { NumberFormatCountryIso } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideChatBot } from '@coin/chat-bot-feature';
import { provideDefaultDateFormats } from '@coin/shared/core/util-helpers';
import { provideToastr } from 'ngx-toastr';
import { APP_ROUTES } from './app.routes';
import { httpInterceptorProviders } from './http-interceptors';
import { DynamicLocaleId } from './shared/models/dynamic-locale-id.model';

registerLocaleData(localeDe, NumberFormatCountryIso.German, localeDeExtra);
registerLocaleData(localeEn, NumberFormatCountryIso.English, localeEnExtra);

function loadIcons(): void {
  const matIconRegistry = inject(MatIconRegistry);
  const domSanitizer = inject(DomSanitizer);
  matIconRegistry.addSvgIcon(
    'manager',
    domSanitizer.bypassSecurityTrustResourceUrl('../../../assets/svgs/org-planning/manager.svg')
  );
  matIconRegistry.addSvgIcon(
    'manager_white',
    domSanitizer.bypassSecurityTrustResourceUrl('../assets/svgs/org-planning/manager_white.svg')
  );
  matIconRegistry.addSvgIcon('org', domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/org.svg'));
  matIconRegistry.addSvgIcon(
    'collapse_all',
    domSanitizer.bypassSecurityTrustResourceUrl('../../assets/svgs/collapse-all.svg')
  );
}

export const APP_CONFIG: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(APP_ROUTES),
    importProvidersFrom(
      MatBadgeModule,
      MatTabsModule,
      BrowserModule,
      BrowserAnimationsModule,
      MatSlideToggleModule,
      MatIconModule,
      FormsModule,
      TranslateModule.forRoot(CustomTranslationLoader.createConfig(AppEnvironment.Customer)),
      AuthGmsAngularClientModule.forRoot({
        ...environment.authentication,
        ...environment.customerAuthentication
      } as AuthGmsClientConfig),
      DragDropModule,
      ApiModuleV2.forRoot(() => {
        return new ApiConfigurationV2({ basePath: environment.api.baseUrl });
      }),
      IncentiveSupportApiModule.forRoot(() => {
        return new IncentiveSupportApiConfiguration({ basePath: environment.api.baseUrl });
      }),
      AuthComponentsModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWithDelay:5000'
      })
    ),
    { provide: AuthUserState, useExisting: UserState },
    httpInterceptorProviders,
    TranslateService,
    { provide: LOCALE_ID, deps: [TranslateService], useClass: DynamicLocaleId },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // Previously came in through SharedModule; importing that NgModule here made all its components eager.
    provideToastr(),
    provideInjectablePipes(),
    provideDefaultDateFormats(),
    provideChatBot(),
    provideTranslateDebugStore(),
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } }, // fix for https://jira.siemens-energy.com/browse/GMTEC-22822
    provideEnvironmentInitializer(loadIcons)
  ]
};
